// Tutor pay by cycle (spec 053): which ended cycles staff can pay, what each tutor earned in
// one, what was paid against it, and the status the Payouts page shows. Pure — no React, no
// Firestore — so Jest loads it.
import { cyclePeriod, lastDayOf, periodLabel, periodRange } from "./payCycle";
import { bookingPaidHours, rateAt } from "./teachingHours";
import { egp } from "./queuePresentation";
import { toDate } from "./ticketPresentation";
import { csvText } from "./transactionPresentation";

// D12: the first cycle the Payouts page pays. functions/payCycle.js holds the same key.
export const FIRST_PAYABLE_KEY = "2026-08-26";
// D5: 051's opening balance cleared every earning before 26 Sep 2026 00:00 Cairo, so a cycle
// that ended by then never moves Owed, and gets no fixed pay (D9).
export const OWED_FROM_KEY = "2026-09-26";
// D4: one JPG, PNG or PDF of at most 10 MB — storage.rules and recordPayout check the same.
export const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
export const RECEIPT_TYPES = ["image/jpeg", "image/png", "application/pdf"];

export const round2 = (n) => Math.round(n * 100) / 100;
const pad = (n) => String(n).padStart(2, "0");

// "2026-08-26": a date in the viewer's zone (Cairo for staff). A cycle's key is its start's —
// the key recordPayout stores and creditFixedPay credits under (D18).
export const cycleKeyOf = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Does paying this cycle lower Owed? Not for one that ended by 26 Sep 2026 00:00 (D5).
export const owedMoves = (cycle) => cycleKeyOf(cycle.end) > OWED_FROM_KEY;

// Every ended cycle from FIRST_PAYABLE_KEY on, the last one first: [{ key, start, end, label }].
// The running cycle is never in it (D7). cyclePeriod repeats its oldest cycle past the rule's
// origin, so a repeated key ends the walk as surely as an early one.
// ponytail: cyclePeriod walks at most ~36 months back (payCycle.js ORIGIN_MONTHS_BACK), so from
// mid-2029 the oldest cycles offered start later than FIRST_PAYABLE_KEY. Widen payCycle.js's
// window (all three copies) if a cycle that old ever needs paying.
export const payableCycles = (versions, now) => {
  const out = [];
  for (let offset = -1; ; offset -= 1) {
    const period = cyclePeriod(versions, now, offset);
    const key = cycleKeyOf(period.start);
    if (key < FIRST_PAYABLE_KEY || out.some((c) => c.key === key)) return out;
    out.push({ key, start: period.start, end: period.end, label: periodRange(period) });
  }
};

// Hours and teaching pay per tutor from one cycle's bookings (those that end inside it): each
// paid lesson's hours at the rate its tutor had when it started (D14). → { [teacherId]: { hours, teaching } }
export const cycleTeaching = (bookings, tutorsById) => {
  const out = {};
  (bookings || []).forEach((b) => {
    const hours = bookingPaidHours(b);
    if (!hours || !b.teacherId) return;
    const row = out[b.teacherId] || (out[b.teacherId] = { hours: 0, teaching: 0 });
    row.hours += hours;
    row.teaching = round2(row.teaching + hours * rateAt(tutorsById[b.teacherId], b.startTimeUTC));
  });
  return out;
};

// What was paid per tutor; an undone payment pays nothing (D6).
export const paidByTutor = (payouts) => {
  const out = {};
  (payouts || []).forEach((p) => {
    if (p.undoneAt || !p.teacherId) return;
    out[p.teacherId] = round2((out[p.teacherId] || 0) + (Number(p.amount) || 0));
  });
  return out;
};

// One tutor's status for a cycle (D1, D3), and what is left to pay.
export const payStatus = (total, paid) => {
  const left = round2(total - paid);
  if (left <= 0) return { key: "paid", label: "Paid", color: "success", left: 0 };
  if (paid > 0) return { key: "part", label: `Part paid · ${egp(left)} left`, color: "warning", left };
  return { key: "unpaid", label: "Not paid", color: "error", left };
};

const STATUS_ORDER = { unpaid: 0, part: 1, paid: 2 };

// No lesson pays: the breakdown of a row with only fixed pay or only a payment.
const noLessons = () => ({ count: 0, taught: { count: 0, hours: 0, pay: 0 }, lateCancels: { count: 0, hours: 0, pay: 0 } });

// The lessons that pay each tutor in a cycle, split as the payout panel's Breakdown shows them
// (044 D118, D121): finished ones, free trials included (047 D88), and cancelled ones that still
// pay (053 D1) — each at the rate its tutor had when it started (D14), rounded as cycleTeaching
// rounds, so taught + late cancels equals the teaching pay.
// → { [teacherId]: { count, taught: { count, hours, pay }, lateCancels: { count, hours, pay } } }
export const cycleLessons = (bookings, tutorsById) => {
  const out = {};
  (bookings || []).forEach((b) => {
    const hours = bookingPaidHours(b);
    if (!hours || !b.teacherId) return;
    const row = out[b.teacherId] || (out[b.teacherId] = noLessons());
    const part = String(b.status || "").toLowerCase() === "finished" ? row.taught : row.lateCancels;
    row.count += 1;
    part.count += 1;
    part.hours += hours;
    part.pay = round2(part.pay + hours * rateAt(tutorsById[b.teacherId], b.startTimeUTC));
  });
  return out;
};

// The Breakdown adds up to Teaching to the piastre (044 R71): cycleTeaching rounds one running
// sum, and two part sums can round a half piastre the other way (a rate with piastres, half-hour
// lessons). Late cancels take the rest when there are any; otherwise taught is Teaching.
const addsUp = (split, teaching) => {
  const taught = split.lateCancels.count ? split.taught.pay : teaching;
  return {
    ...split,
    taught: { ...split.taught, pay: taught },
    lateCancels: { ...split.lateCancels, pay: round2(teaching - taught) },
  };
};

// The "Pay for [cycle]" rows (D12): every real tutor (D11) whose total or paid for the cycle is
// above zero; what is left to pay first, then by name (R6). `lessons` is cycleLessons' map: each
// row carries its lesson count and breakdown (044 D118, D121).
export const cyclePayRows = ({ tutors, teaching, payouts, cycleKey, lessons = {} }) => {
  const paid = paidByTutor((payouts || []).filter((p) => p.cycleKey === cycleKey));
  return (tutors || [])
    .filter((t) => !t.isInternal)
    .map((t) => {
      const earned = teaching[t.id] || { hours: 0, teaching: 0 };
      const fixed = round2(Number(t.fixedPayByCycle?.[cycleKey]) || 0);
      const total = round2(earned.teaching + fixed);
      const done = paid[t.id] || 0;
      return {
        tutor: t,
        hours: earned.hours,
        teaching: earned.teaching,
        lessons: lessons[t.id]?.count || 0,
        breakdown: addsUp(lessons[t.id] || noLessons(), earned.teaching),
        fixed,
        total,
        paid: done,
        status: payStatus(total, done),
      };
    })
    .filter((r) => r.total > 0 || r.paid > 0)
    .sort(
      (a, b) =>
        STATUS_ORDER[a.status.key] - STATUS_ORDER[b.status.key] ||
        String(a.tutor.name || "").localeCompare(String(b.tutor.name || ""))
    );
};

// Owed over real tutors (D11), from the tutor list the Payouts page holds.
export const owedNow = (tutors) => {
  const owing = (tutors || []).filter((t) => !t.isInternal && Number(t.unpaidEarning) > 0);
  return { count: owing.length, total: round2(owing.reduce((sum, t) => sum + Number(t.unpaidEarning), 0)) };
};

// The same for an aggregate over every user with unpaidEarning > 0 (the Queue, Reports): minus
// the internal accounts among them, read apart (R14). A `!=` filter would also drop every
// document that has no isInternal field, which is nearly all of them.
export const withoutInternal = ({ count, total }, internals) => {
  const owing = (internals || []).filter((u) => Number(u.unpaidEarning) > 0);
  return {
    count: Math.max(0, (Number(count) || 0) - owing.length),
    total: Math.max(0, round2((Number(total) || 0) - owing.reduce((sum, u) => sum + Number(u.unpaidEarning), 0))),
  };
};

// Why a chosen receipt cannot be sent, or "" (D4).
export const receiptProblem = (file) => {
  if (!file) return "Choose the transfer receipt.";
  if (!RECEIPT_TYPES.includes(file.type)) return "The receipt must be a JPG, PNG or PDF file.";
  if (file.size > MAX_RECEIPT_BYTES) return "The receipt must be at most 10 MB.";
  return "";
};

// Why an amount cannot be paid, or "" (D3): above zero and at most what is left.
export const amountProblem = (amount, left) => {
  const n = round2(Number(amount));
  if (!Number.isFinite(n) || n <= 0) return "Enter an amount above zero.";
  if (n > round2(left)) return `At most ${egp(left)} is left for this cycle.`;
  return "";
};

// "26 Aug – 25 Sep" for a payment's cycle; "—" for one recorded before cycles (023, 051).
export const payoutCycleLabel = (payout = {}) => {
  const start = toDate(payout.cycleStart);
  const end = toDate(payout.cycleEnd);
  return start && end ? periodRange({ start, end }) : "—";
};

// ── The Payouts page in Figma's look (044 D114–D125) ──

export const PAY_TABS = [
  { key: "all", label: "All" },
  { key: "unpaid", label: "Not paid" },
  { key: "part", label: "Part paid" },
  { key: "paid", label: "Paid" },
];

// The tabs' counts over the cycle's rows (D116).
export const payTabCounts = (rows) => {
  const counts = { all: rows.length, unpaid: 0, part: 0, paid: 0 };
  rows.forEach((r) => {
    counts[r.status.key] += 1;
  });
  return counts;
};

// The rows a tab and the search leave (D116, D117): the search matches the name or the email in
// any case, or the phone by its digits alone ("100 000" finds "+20 100 000 0000").
export const payRowsShown = (rows, tab, search) => {
  const q = String(search || "").trim().toLowerCase();
  const digits = q.replace(/\D/g, "");
  return rows.filter((r) => {
    if (tab !== "all" && r.status.key !== tab) return false;
    if (!q) return true;
    const t = r.tutor;
    return (
      String(t.name || "").toLowerCase().includes(q) ||
      String(t.email || "").toLowerCase().includes(q) ||
      (digits.length > 0 && String(t.phone || "").replace(/\D/g, "").includes(digits))
    );
  });
};

// The "This cycle" card's numbers (D115): its tutors, total, what is left, and the tutors by status.
export const cycleSummary = (rows) =>
  rows.reduce(
    (s, r) => ({
      ...s,
      tutors: s.tutors + 1,
      total: round2(s.total + r.total),
      left: round2(s.left + r.status.left),
      [r.status.key]: s[r.status.key] + 1,
    }),
    { tutors: 0, total: 0, left: 0, unpaid: 0, part: 0, paid: 0 }
  );

// "25 Sep": the day a cycle ended. Its `end` is exclusive (payCycle's lastDayOf), and
// periodLabel names the month that last day falls in.
export const cycleEndedLabel = (cycle) => `${lastDayOf(cycle).getDate()} ${periodLabel(cycle)}`;

const endTime = (b) => toDate(b.endTimeUTC)?.getTime() || 0;

// The panel's lessons (D121): this tutor's lessons that pay, the latest first, each with its pay
// at the rate the tutor had when it started (053 D14). A cancelled one that pays is a late cancel.
export const tutorCycleLessons = (bookings, tutor) =>
  (bookings || [])
    .filter((b) => b.teacherId === tutor.id && bookingPaidHours(b) > 0)
    .map((b) => {
      const hours = bookingPaidHours(b);
      return {
        booking: b,
        hours,
        pay: round2(hours * rateAt(tutor, b.startTimeUTC)),
        lateCancel: String(b.status || "").toLowerCase() !== "finished",
      };
    })
    .sort((x, y) => endTime(y.booking) - endTime(x.booking));

// The panel's payments (D121, in place of 053's Receipts dialog): this tutor's in the cycle,
// the newest first; an undone one stays, marked.
export const tutorCyclePayments = (payouts, tutorId) =>
  (payouts || [])
    .filter((p) => p.teacherId === tutorId)
    .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0));

const STATUS_WORDS = { unpaid: "Not paid", part: "Part paid", paid: "Paid" };
const PAYOUTS_HEADER = ["Tutor", "Email", "Phone", "Lessons", "Hours", "Teaching (E£)", "Fixed (E£)", "Total (E£)", "Paid (E£)", "Left (E£)", "Status"];

// Export (D124): every row of the cycle, whatever the tab or search; numbers as numbers, through
// Transactions' CSV writer (Excel's byte-order mark, the formula guard).
export const payoutsCsv = (rows, cycle, now = new Date()) => ({
  text: csvText(
    PAYOUTS_HEADER,
    rows.map((r) => [
      r.tutor.name || r.tutor.id,
      r.tutor.email || "",
      r.tutor.phone || "",
      r.lessons,
      round2(r.hours),
      r.teaching,
      r.fixed,
      r.total,
      r.paid,
      r.status.left,
      STATUS_WORDS[r.status.key],
    ])
  ),
  filename: `payouts-${cycle.key}-${cycleKeyOf(now)}.csv`,
});

// The cycle a link names (`?cycle=`), or the last ended one (D120).
export const pickCycle = (cycles, key) => cycles.find((c) => c.key === key) || cycles[0] || null;
